/**
 * GRANULLO PLATFORM EVOLUTION — Decision Matrix Context
 * Phase 3: Decision Matrix Engine
 * 
 * FOUNDER DIRECTIVE: Active governance infrastructure
 * 
 * AUTHORITY ROUTING:
 * - warning → local/node authority
 * - critical → policy authority
 * - emergency → sponsor/program authority
 * - catastrophic → regulator/supervisory authority
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { THRESHOLD_LEVELS } from './BaselineSignalsContext';

export const DECISION_STATUS = {
  PENDING: 'pending',
  ACKNOWLEDGED: 'acknowledged',
  RESOLVED: 'resolved',
  ESCALATED: 'escalated',
  CLOSED: 'closed'
};

export const DECISION_ACTIONS = {
  INFORMATIONAL_ALERT: 'informational_alert',
  WARNING_ALERT: 'warning_alert',
  INTERVENTION_RECOMMENDATION: 'intervention_recommendation',
  ENHANCED_MONITORING: 'enhanced_monitoring',
  REGIONAL_REVIEW: 'regional_review',
  AUTHORITY_ESCALATION: 'authority_escalation',
  POLICY_ENFORCEMENT: 'policy_enforcement',
  SIMULATION_TRIGGER: 'simulation_trigger',
  MERCHANT_FREEZE: 'merchant_freeze',
  BENEFICIARY_SLOWDOWN: 'beneficiary_slowdown',
  PROGRAM_SUSPENSION: 'program_suspension'
};

export const AUTHORITY_LEVELS = {
  LOCAL_NODE: 'local_node',
  POLICY_AUTHORITY: 'policy_authority',
  SPONSOR_AUTHORITY: 'sponsor_authority',
  REGULATOR_AUTHORITY: 'regulator_authority'
};

export const DECISION_ACTION_METADATA = {
  [DECISION_ACTIONS.INFORMATIONAL_ALERT]: { label: 'Informational Alert', severity: 'low', requiresAcknowledgment: false },
  [DECISION_ACTIONS.WARNING_ALERT]: { label: 'Warning Alert', severity: 'medium', requiresAcknowledgment: true },
  [DECISION_ACTIONS.INTERVENTION_RECOMMENDATION]: { label: 'Intervention Recommended', severity: 'high', requiresAcknowledgment: true },
  [DECISION_ACTIONS.ENHANCED_MONITORING]: { label: 'Enhanced Monitoring', severity: 'medium', requiresAcknowledgment: true },
  [DECISION_ACTIONS.REGIONAL_REVIEW]: { label: 'Regional Review', severity: 'high', requiresAcknowledgment: true },
  [DECISION_ACTIONS.AUTHORITY_ESCALATION]: { label: 'Authority Escalation', severity: 'critical', requiresAcknowledgment: true },
  [DECISION_ACTIONS.POLICY_ENFORCEMENT]: { label: 'Policy Enforcement', severity: 'critical', requiresAcknowledgment: true },
  [DECISION_ACTIONS.SIMULATION_TRIGGER]: { label: 'Simulation Trigger', severity: 'medium', requiresAcknowledgment: false },
  [DECISION_ACTIONS.MERCHANT_FREEZE]: { label: 'Merchant Freeze', severity: 'critical', requiresAcknowledgment: true },
  [DECISION_ACTIONS.BENEFICIARY_SLOWDOWN]: { label: 'Beneficiary Slowdown', severity: 'high', requiresAcknowledgment: true },
  [DECISION_ACTIONS.PROGRAM_SUSPENSION]: { label: 'Program Suspension', severity: 'catastrophic', requiresAcknowledgment: true }
};

export function createDecisionEvent({ programId, triggeringSignal, deviationLevel, deviationValue, nodeId = null, recommendedAction, authorityResponsible, escalationChain = [], responseDeadlineHours = 24, metadata = {} }) {
  const now = new Date();
  const deadline = new Date(now.getTime() + (responseDeadlineHours * 60 * 60 * 1000));
  return {
    decision_id: `decision_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    program_id: programId, triggering_signal: triggeringSignal, deviation_level: deviationLevel, deviation_value: deviationValue, node_id: nodeId,
    recommended_action: recommendedAction, authority_responsible: authorityResponsible, escalation_chain: escalationChain, response_deadline: deadline.toISOString(),
    decision_status: DECISION_STATUS.PENDING, acknowledged_by: null, acknowledged_at: null, resolved_by: null, resolved_at: null,
    simulation_attachment_ref: null, simulation_outcomes: [], outcome_ref: null, audit_ref: `audit_${Date.now()}`,
    audit_log: [{ timestamp: now.toISOString(), action: 'created', actor: 'system', details: { triggeringSignal, deviationLevel, recommendedAction } }],
    metadata, created_at: now.toISOString(), updated_at: now.toISOString()
  };
}

export function createDecisionMatrixEntry({ id, programId, name, description = '', signalCategory = null, signalId = null, nodeType = null, thresholdLevel, recommendedAction, authorityLevel, responseWindowHours = 24, escalationEnabled = true, simulationRequired = false, policyRuleRefs = [], enabled = true }) {
  return {
    id: id || `dm_entry_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    program_id: programId, name, description,
    trigger_conditions: { signal_category: signalCategory, signal_id: signalId, node_type: nodeType, threshold_level: thresholdLevel },
    response_config: { recommended_action: recommendedAction, authority_level: authorityLevel, response_window_hours: responseWindowHours, escalation_enabled: escalationEnabled, simulation_required: simulationRequired },
    policy_rule_refs: policyRuleRefs, enabled, trigger_count: 0, last_triggered_at: null,
    created_at: new Date().toISOString(), updated_at: new Date().toISOString()
  };
}

export function routeToAuthority(thresholdLevel, blueprintGraph) {
  const authorityMap = { [THRESHOLD_LEVELS.NORMAL]: null, [THRESHOLD_LEVELS.WARNING]: AUTHORITY_LEVELS.LOCAL_NODE, [THRESHOLD_LEVELS.CRITICAL]: AUTHORITY_LEVELS.POLICY_AUTHORITY, [THRESHOLD_LEVELS.EMERGENCY]: AUTHORITY_LEVELS.SPONSOR_AUTHORITY, [THRESHOLD_LEVELS.CATASTROPHIC]: AUTHORITY_LEVELS.REGULATOR_AUTHORITY };
  const authorityLevel = authorityMap[thresholdLevel];
  if (!authorityLevel) return null;
  let authorityNode = null, escalationChain = [];
  if (blueprintGraph?.nodes) {
    const authorityNodes = blueprintGraph.nodes.filter(n => ['authority_node', 'regulator', 'auditor', 'sponsor', 'program_sponsor'].includes(n.type));
    if (authorityLevel === AUTHORITY_LEVELS.LOCAL_NODE) { authorityNode = authorityNodes.find(n => n.type === 'authority_node') || authorityNodes[0]; escalationChain = authorityNodes.slice(0, 2).map(n => n.id); }
    else if (authorityLevel === AUTHORITY_LEVELS.POLICY_AUTHORITY) { authorityNode = authorityNodes.find(n => n.type === 'sponsor' || n.type === 'program_sponsor') || authorityNodes[0]; escalationChain = authorityNodes.slice(0, 3).map(n => n.id); }
    else if (authorityLevel === AUTHORITY_LEVELS.SPONSOR_AUTHORITY) { authorityNode = authorityNodes.find(n => n.type === 'sponsor') || authorityNodes.find(n => n.type === 'program_sponsor'); escalationChain = authorityNodes.map(n => n.id); }
    else if (authorityLevel === AUTHORITY_LEVELS.REGULATOR_AUTHORITY) { authorityNode = authorityNodes.find(n => n.type === 'regulator') || authorityNodes.find(n => n.type === 'auditor') || authorityNodes[0]; escalationChain = authorityNodes.map(n => n.id); }
  }
  const responseWindows = { [THRESHOLD_LEVELS.WARNING]: 72, [THRESHOLD_LEVELS.CRITICAL]: 24, [THRESHOLD_LEVELS.EMERGENCY]: 4, [THRESHOLD_LEVELS.CATASTROPHIC]: 1 };
  return { authorityLevel, authorityNode: authorityNode?.id || null, authorityNodeName: authorityNode?.name || `${authorityLevel} (default)`, authorityNodeType: authorityNode?.type || 'system', escalationChain, responseWindowHours: responseWindows[thresholdLevel] || 24 };
}

export function determineRecommendedAction(thresholdLevel, signalCategory) {
  const defaults = { [THRESHOLD_LEVELS.WARNING]: DECISION_ACTIONS.WARNING_ALERT, [THRESHOLD_LEVELS.CRITICAL]: DECISION_ACTIONS.INTERVENTION_RECOMMENDATION, [THRESHOLD_LEVELS.EMERGENCY]: DECISION_ACTIONS.AUTHORITY_ESCALATION, [THRESHOLD_LEVELS.CATASTROPHIC]: DECISION_ACTIONS.PROGRAM_SUSPENSION };
  if (thresholdLevel === THRESHOLD_LEVELS.CRITICAL) { if (signalCategory === 'concentration') return DECISION_ACTIONS.REGIONAL_REVIEW; if (signalCategory === 'velocity') return DECISION_ACTIONS.BENEFICIARY_SLOWDOWN; if (signalCategory === 'compliance') return DECISION_ACTIONS.POLICY_ENFORCEMENT; }
  if (thresholdLevel === THRESHOLD_LEVELS.EMERGENCY) { if (signalCategory === 'concentration') return DECISION_ACTIONS.MERCHANT_FREEZE; if (signalCategory === 'capacity') return DECISION_ACTIONS.ENHANCED_MONITORING; }
  return defaults[thresholdLevel] || DECISION_ACTIONS.INFORMATIONAL_ALERT;
}

export function createSimulationAttachment({ decisionEventId, interventionOptions = [], baselineProjection = null }) {
  return { id: `sim_attach_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, decision_event_id: decisionEventId, intervention_options: interventionOptions.map((opt, idx) => ({ option_id: `opt_${idx}`, action: opt.action, description: opt.description, projected_outcome: null, simulation_run_id: null })), baseline_projection: baselineProjection, comparison_results: null, recommended_option: null, status: 'pending', created_at: new Date().toISOString() };
}

const DecisionMatrixContext = createContext(null);

export function DecisionMatrixProvider({ children }) {
  const [activeDecisionEvents, setActiveDecisionEvents] = useState([]);
  const [decisionHistory, setDecisionHistory] = useState([]);
  const [simulationAttachments, setSimulationAttachments] = useState({});
  const [decisionMatrix, setDecisionMatrix] = useState({ entries: [], escalationPaths: [] });
  const [blueprintGraph, setBlueprintGraph] = useState(null);
  
  const addMatrixEntry = useCallback((entryData) => {
    const entry = createDecisionMatrixEntry({ ...entryData, programId: entryData.programId || 'demo' });
    setDecisionMatrix(prev => ({ ...prev, entries: [...prev.entries, entry] }));
    return entry;
  }, []);
  
  const updateMatrixEntry = useCallback((entryId, updates) => {
    setDecisionMatrix(prev => ({
      ...prev,
      entries: prev.entries.map(e => e.id === entryId ? { ...e, ...updates, updated_at: new Date().toISOString() } : e)
    }));
  }, []);
  
  const removeMatrixEntry = useCallback((entryId) => {
    setDecisionMatrix(prev => ({ ...prev, entries: prev.entries.filter(e => e.id !== entryId) }));
  }, []);
  
  const addEscalationPath = useCallback((path) => {
    const newPath = { id: `esc_path_${Date.now()}`, ...path, created_at: new Date().toISOString() };
    setDecisionMatrix(prev => ({ ...prev, escalationPaths: [...prev.escalationPaths, newPath] }));
    return newPath;
  }, []);
  
  const setBlueprint = useCallback((blueprint) => {
    setBlueprintGraph(blueprint);
  }, []);
  
  const getInterventionOptions = (thresholdLevel, signalCategory) => {
    const options = [];
    if (thresholdLevel === THRESHOLD_LEVELS.CRITICAL || thresholdLevel === THRESHOLD_LEVELS.EMERGENCY) {
      options.push({ action: DECISION_ACTIONS.ENHANCED_MONITORING, description: 'Increase monitoring frequency' });
    }
    if (signalCategory === 'concentration') {
      options.push({ action: DECISION_ACTIONS.MERCHANT_FREEZE, description: 'Freeze top merchant transactions' });
      options.push({ action: DECISION_ACTIONS.REGIONAL_REVIEW, description: 'Regional activity review' });
    }
    if (signalCategory === 'velocity') {
      options.push({ action: DECISION_ACTIONS.BENEFICIARY_SLOWDOWN, description: 'Rate-limit beneficiary transactions' });
    }
    if (thresholdLevel === THRESHOLD_LEVELS.CATASTROPHIC) {
      options.push({ action: DECISION_ACTIONS.PROGRAM_SUSPENSION, description: 'Full program suspension' });
    }
    return options;
  };
  
  const processSignalDrift = useCallback(({ signalId, signalName, signalCategory, programId, nodeId, deviationValue, deviationPercent, thresholdCrossed, timestamp = new Date().toISOString(), originatingSource }) => {
    if (thresholdCrossed === THRESHOLD_LEVELS.NORMAL) return null;
    
    const matchingEntry = decisionMatrix.entries.find(entry => {
      if (!entry.enabled) return false;
      const c = entry.trigger_conditions;
      return c.threshold_level === thresholdCrossed && (!c.signal_category || c.signal_category === signalCategory) && (!c.signal_id || c.signal_id === signalId);
    });
    
    const authorityRouting = routeToAuthority(thresholdCrossed, blueprintGraph);
    const recommendedAction = matchingEntry?.response_config?.recommended_action || determineRecommendedAction(thresholdCrossed, signalCategory);
    
    const decisionEvent = createDecisionEvent({
      programId,
      triggeringSignal: { signal_id: signalId, signal_name: signalName, signal_category: signalCategory, originating_source: originatingSource, timestamp },
      deviationLevel: thresholdCrossed, deviationValue, nodeId, recommendedAction,
      authorityResponsible: { authority_level: authorityRouting?.authorityLevel, authority_node_id: authorityRouting?.authorityNode, authority_node_name: authorityRouting?.authorityNodeName, authority_node_type: authorityRouting?.authorityNodeType },
      escalationChain: authorityRouting?.escalationChain || [],
      responseDeadlineHours: matchingEntry?.response_config?.response_window_hours || authorityRouting?.responseWindowHours || 24,
      metadata: { deviation_percent: deviationPercent, matched_matrix_entry: matchingEntry?.id || null }
    });
    
    if (matchingEntry) {
      updateMatrixEntry(matchingEntry.id, { trigger_count: matchingEntry.trigger_count + 1, last_triggered_at: new Date().toISOString() });
    }
    
    setActiveDecisionEvents(prev => [...prev, decisionEvent]);
    
    if (matchingEntry?.response_config?.simulation_required) {
      const simAttachment = createSimulationAttachment({ decisionEventId: decisionEvent.decision_id, interventionOptions: getInterventionOptions(thresholdCrossed, signalCategory) });
      setSimulationAttachments(prev => ({ ...prev, [decisionEvent.decision_id]: simAttachment }));
    }
    
    return decisionEvent;
  }, [decisionMatrix, blueprintGraph, updateMatrixEntry]);
  
  const acknowledgeEvent = useCallback((decisionId, acknowledgedBy) => {
    setActiveDecisionEvents(prev => prev.map(event => event.decision_id !== decisionId ? event : {
      ...event, decision_status: DECISION_STATUS.ACKNOWLEDGED, acknowledged_by: acknowledgedBy, acknowledged_at: new Date().toISOString(),
      audit_log: [...event.audit_log, { timestamp: new Date().toISOString(), action: 'acknowledged', actor: acknowledgedBy, details: {} }],
      updated_at: new Date().toISOString()
    }));
  }, []);
  
  const resolveEvent = useCallback((decisionId, resolvedBy, resolution) => {
    setActiveDecisionEvents(prev => {
      const event = prev.find(e => e.decision_id === decisionId);
      if (!event) return prev;
      const resolvedEvent = { ...event, decision_status: DECISION_STATUS.RESOLVED, resolved_by: resolvedBy, resolved_at: new Date().toISOString(), outcome_ref: resolution.outcome_ref || null, audit_log: [...event.audit_log, { timestamp: new Date().toISOString(), action: 'resolved', actor: resolvedBy, details: resolution }], updated_at: new Date().toISOString() };
      setDecisionHistory(hist => [...hist, resolvedEvent]);
      return prev.filter(e => e.decision_id !== decisionId);
    });
  }, []);
  
  const escalateEvent = useCallback((decisionId, escalatedBy, reason) => {
    setActiveDecisionEvents(prev => prev.map(event => {
      if (event.decision_id !== decisionId) return event;
      const currentIdx = event.escalation_chain.indexOf(event.authority_responsible.authority_node_id);
      const nextId = event.escalation_chain[currentIdx + 1] || event.escalation_chain[event.escalation_chain.length - 1];
      return { ...event, decision_status: DECISION_STATUS.ESCALATED, authority_responsible: { ...event.authority_responsible, authority_node_id: nextId, escalated_from: event.authority_responsible.authority_node_id }, audit_log: [...event.audit_log, { timestamp: new Date().toISOString(), action: 'escalated', actor: escalatedBy, details: { reason, escalated_to: nextId } }], updated_at: new Date().toISOString() };
    }));
  }, []);
  
  const closeEvent = useCallback((decisionId, closedBy, notes) => {
    setActiveDecisionEvents(prev => {
      const event = prev.find(e => e.decision_id === decisionId);
      if (!event) return prev;
      const closedEvent = { ...event, decision_status: DECISION_STATUS.CLOSED, audit_log: [...event.audit_log, { timestamp: new Date().toISOString(), action: 'closed', actor: closedBy, details: { notes } }], updated_at: new Date().toISOString() };
      setDecisionHistory(hist => [...hist, closedEvent]);
      return prev.filter(e => e.decision_id !== decisionId);
    });
  }, []);
  
  const eventsByStatus = useMemo(() => {
    const grouped = { [DECISION_STATUS.PENDING]: [], [DECISION_STATUS.ACKNOWLEDGED]: [], [DECISION_STATUS.ESCALATED]: [] };
    activeDecisionEvents.forEach(e => { if (grouped[e.decision_status]) grouped[e.decision_status].push(e); });
    return grouped;
  }, [activeDecisionEvents]);
  
  const eventsBySeverity = useMemo(() => {
    const grouped = { [THRESHOLD_LEVELS.WARNING]: [], [THRESHOLD_LEVELS.CRITICAL]: [], [THRESHOLD_LEVELS.EMERGENCY]: [], [THRESHOLD_LEVELS.CATASTROPHIC]: [] };
    activeDecisionEvents.forEach(e => { if (grouped[e.deviation_level]) grouped[e.deviation_level].push(e); });
    return grouped;
  }, [activeDecisionEvents]);
  
  const decisionSummary = useMemo(() => {
    const pending = eventsByStatus[DECISION_STATUS.PENDING].length;
    const acknowledged = eventsByStatus[DECISION_STATUS.ACKNOWLEDGED].length;
    const escalated = eventsByStatus[DECISION_STATUS.ESCALATED].length;
    const catastrophic = eventsBySeverity[THRESHOLD_LEVELS.CATASTROPHIC]?.length || 0;
    const emergency = eventsBySeverity[THRESHOLD_LEVELS.EMERGENCY]?.length || 0;
    const critical = eventsBySeverity[THRESHOLD_LEVELS.CRITICAL]?.length || 0;
    const warning = eventsBySeverity[THRESHOLD_LEVELS.WARNING]?.length || 0;
    const now = new Date();
    const overdue = activeDecisionEvents.filter(e => new Date(e.response_deadline) < now && e.decision_status === DECISION_STATUS.PENDING).length;
    return { totalActive: activeDecisionEvents.length, pending, acknowledged, escalated, overdue, catastrophic, emergency, critical, warning, matrixEntriesCount: decisionMatrix.entries.length, escalationPathsCount: decisionMatrix.escalationPaths.length, historicalCount: decisionHistory.length };
  }, [activeDecisionEvents, eventsByStatus, eventsBySeverity, decisionMatrix, decisionHistory]);
  
  const governanceHealth = useMemo(() => {
    const { catastrophic, emergency, overdue } = decisionSummary;
    let status = 'healthy';
    if (catastrophic > 0) status = 'critical';
    else if (emergency > 0 || overdue > 0) status = 'warning';
    return { status, hasUnresolvedCatastrophic: catastrophic > 0, hasUnresolvedEmergency: emergency > 0, hasOverdueEvents: overdue > 0, requiresImmediateAttention: catastrophic > 0 || overdue > 0 };
  }, [decisionSummary]);
  
  const value = {
    DECISION_STATUS, DECISION_ACTIONS, DECISION_ACTION_METADATA, AUTHORITY_LEVELS,
    decisionMatrix, blueprintGraph, setBlueprint,
    activeDecisionEvents, decisionHistory, simulationAttachments,
    eventsByStatus, eventsBySeverity, decisionSummary, governanceHealth,
    addMatrixEntry, updateMatrixEntry, removeMatrixEntry, addEscalationPath,
    processSignalDrift, acknowledgeEvent, resolveEvent, escalateEvent, closeEvent,
    routeToAuthority, determineRecommendedAction,
    createDecisionEvent, createDecisionMatrixEntry, createSimulationAttachment
  };
  
  return <DecisionMatrixContext.Provider value={value}>{children}</DecisionMatrixContext.Provider>;
}

export function useDecisionMatrix() {
  const context = useContext(DecisionMatrixContext);
  if (!context) throw new Error('useDecisionMatrix must be used within DecisionMatrixProvider');
  return context;
}

export default DecisionMatrixContext;
